import clsx from "clsx";
import type { FaClientDataGathering } from "@/services/api/agentic-v2/types";

function formatShortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function adviceUrgencyLabel(days: number | null | undefined): string | null {
  if (days == null) return null;
  if (days < 0) return `${Math.abs(days)}d since advice session`;
  if (days === 0) return "Advice session today";
  if (days <= 7) return `${days}d until advice session`;
  return `${days}d until advice session`;
}

function ProgressMetric({
  label,
  detail,
  pct,
  urgent = false,
}: {
  label: string;
  detail: string;
  pct: number;
  urgent?: boolean;
}) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {label}
        </span>
        <span
          className={clsx(
            "text-xs tabular-nums font-medium text-slate-700 dark:text-slate-200",
            urgent && "text-amber-700 dark:text-amber-300",
          )}
        >
          {detail}
        </span>
      </div>
      <div
        className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200/80 dark:bg-white/10"
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label}: ${detail}`}
      >
        <div
          className={clsx(
            "h-full rounded-full transition-all",
            urgent ? "bg-amber-500 dark:bg-amber-400" : "bg-indigo-500 dark:bg-indigo-400",
          )}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}

/** Phase context + plan-level progress metrics (no outer card — parent wraps). */
export function DataGatheringPlanProgress({ summary }: { summary: FaClientDataGathering }) {
  const adviceLabel = adviceUrgencyLabel(summary.days_until_advice);
  const adviceDate = formatShortDate(summary.advice_session_date);
  const tobDate = formatShortDate(summary.tob_signed_date);
  const docsPct =
    summary.plans_total > 0
      ? Math.round((summary.plans_with_docs / summary.plans_total) * 100)
      : 0;
  const cheatPct =
    summary.plans_total > 0
      ? Math.round((summary.plans_with_cheat_sheet / summary.plans_total) * 100)
      : 0;

  const urgent =
    summary.days_until_advice != null &&
    summary.days_until_advice <= 7 &&
    summary.value_covered_pct < 80;

  return (
    <div data-testid="data-gathering-plan-progress">
      <div>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Data gathering phase</h3>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          ToB signed{tobDate ? ` ${tobDate}` : ""}
          {adviceDate ? ` · Advice session ${adviceDate}` : " · No advice session booked"}
        </p>
        {adviceLabel ? (
          <p
            className={clsx(
              "mt-1 text-xs",
              urgent
                ? "font-medium text-amber-700 dark:text-amber-300"
                : "text-slate-500 dark:text-slate-400",
            )}
          >
            {adviceLabel}
          </p>
        ) : null}
      </div>

      <h4 className="mt-4 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Plan progress
      </h4>
      <div className="mt-2 grid gap-4 sm:grid-cols-3">
        <ProgressMetric
          label="Value documented"
          detail={`${summary.value_covered_pct}%`}
          pct={summary.value_covered_pct}
          urgent={urgent}
        />
        <ProgressMetric
          label="Plan documents"
          detail={`${summary.plans_with_docs}/${summary.plans_total} (${docsPct}%)`}
          pct={docsPct}
        />
        <ProgressMetric
          label="Cheat sheets"
          detail={`${summary.plans_with_cheat_sheet}/${summary.plans_total} (${cheatPct}%)`}
          pct={cheatPct}
        />
      </div>
    </div>
  );
}

/** @deprecated Use DataGatheringPlanProgress inside DataGatheringTab layout. */
export function DataGatheringPlansBanner({ summary }: { summary: FaClientDataGathering }) {
  return <DataGatheringPlanProgress summary={summary} />;
}
